import React, { useMemo } from "react";
import "./ReleasePlayerModal.css";

export default function ReleasePlayerModal({ player, roster, onClose, onConfirm, isLoading }) {
  const stats = useMemo(() => {
    const SALARY_CAP = 200; // $200M per team

    // Calculate current roster stats
    const parseContract = (contract) => {
      if (!contract) return 0;
      const parts = contract.split("/");
      const salary = parseFloat(parts[1]) || 0;
      return salary;
    };

    // Current stats (with player)
    const currentSalary = roster.reduce((sum, p) => sum + parseContract(p.contract), 0);
    const currentPositionCounts = {};
    roster.forEach((p) => {
      const pos = p.position || "Unknown";
      currentPositionCounts[pos] = (currentPositionCounts[pos] || 0) + 1;
    });

    // After-release stats (without player)
    const rosterAfterRelease = roster.filter((p) => p._id !== player._id);
    const salaryAfterRelease = rosterAfterRelease.reduce((sum, p) => sum + parseContract(p.contract), 0);
    const positionCountsAfterRelease = {};
    rosterAfterRelease.forEach((p) => {
      const pos = p.position || "Unknown";
      positionCountsAfterRelease[pos] = (positionCountsAfterRelease[pos] || 0) + 1;
    });

    // Get all unique positions
    const allPositions = new Set([...Object.keys(currentPositionCounts), ...Object.keys(positionCountsAfterRelease)]);

    return {
      currentSalary,
      salaryAfterRelease,
      salaryRelieved: currentSalary - salaryAfterRelease,
      currentCapUsage: (currentSalary / SALARY_CAP) * 100,
      afterCapUsage: (salaryAfterRelease / SALARY_CAP) * 100,
      currentRosterSize: roster.length,
      afterRosterSize: rosterAfterRelease.length,
      currentPositionCounts,
      positionCountsAfterRelease,
      allPositions: Array.from(allPositions).sort(),
      SALARY_CAP,
    };
  }, [player, roster]);

  const playerSalary = useMemo(() => {
    if (!player.contract) return 0;
    const parts = player.contract.split("/");
    return parseFloat(parts[1]) || 0;
  }, [player]);

  const contractYears = useMemo(() => {
    if (!player.contract) return "N/A";
    const parts = player.contract.split("/");
    return parts[0] || "N/A";
  }, [player]);

  return (
    <div className="release-overlay" onClick={onClose}>
      <div className="release-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="release-modal-header">
          <h2 className="release-modal-title">Release Player</h2>
          <button className="release-close-btn" onClick={onClose} title="Close">
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="release-modal-body">
          {/* Player Card */}
          <div className="release-player-card">
            <div className="release-player-info">
              <h3>{player.displayName || player.name || "Unknown Player"}</h3>
              <div className="release-player-details">
                <span className="release-detail-item">
                  <strong>Position:</strong> {player.position || "N/A"}
                </span>
                <span className="release-detail-item">
                  <strong>Contract Length:</strong> {contractYears} years
                </span>
                <span className="release-detail-item">
                  <strong>Salary:</strong> ${playerSalary.toFixed(1)}M/year
                </span>
              </div>
            </div>
            <div className="release-warning">
              This player will become a free agent
            </div>
          </div>

          {/* Impact Summary */}
          <div className="release-impact-section">
            <h4>Impact on Your Team</h4>

            {/* Salary Impact */}
            <div className="release-stat-group">
              <div className="release-stat-label">Salary Cap</div>
              <div className="release-stat-comparison">
                <div className="release-stat-item">
                  <span className="stat-label">Current</span>
                  <span className="stat-value">
                    ${stats.currentSalary.toFixed(1)}M ({stats.currentCapUsage.toFixed(1)}%)
                  </span>
                </div>
                <div className="release-stat-arrow">→</div>
                <div className="release-stat-item after">
                  <span className="stat-label">After Release</span>
                  <span className="stat-value">
                    ${stats.salaryAfterRelease.toFixed(1)}M ({stats.afterCapUsage.toFixed(1)}%)
                  </span>
                </div>
              </div>
              <div className="release-stat-relief">
                Freed: ${stats.salaryRelieved.toFixed(1)}M / {stats.SALARY_CAP}M
              </div>
            </div>


            {/* Position Breakdown */}
            <div className="release-stat-group">
              <div className="release-stat-label">Position Breakdown</div>
              <div className="release-positions-grid">
                {stats.allPositions.map((pos) => {
                  const isAffected = (stats.positionCountsAfterRelease[pos] || 0) < (stats.currentPositionCounts[pos] || 0);
                  return (
                    <div key={pos} className={`release-position-item ${isAffected ? "affected" : ""}`}>
                      <span className="position-label">{pos}</span>
                      <div className="position-counts">
                        <span className="count-before">{stats.currentPositionCounts[pos] || 0}</span>
                        <span className="count-arrow">→</span>
                        <span className="count-after">{stats.positionCountsAfterRelease[pos] || 0}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Confirmation Warning */}
          <div className="release-confirmation-warning">
            Are you sure you want to release {player.displayName || player.name}? This action cannot be undone.
          </div>
        </div>

        {/* Footer */}
        <div className="release-modal-footer">
          <button
            className="release-btn-cancel"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className="release-btn-confirm"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? "Releasing..." : "Release Player"}
          </button>
        </div>
      </div>
    </div>
  );
}
